import React, { useState, useEffect } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Users,
  Clock,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import api from "../api/axios";
import { aISO, diaISO } from "../utils/fecha";
import { Card } from "./shared/Card";
import { Button } from "./shared/Button";
import { StatusBadge } from "./shared/Badge";

export default function VisitCalendar() {
  const [view, setView] = useState("month"); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredVisit, setHoveredVisit] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [expandedDays, setExpandedDays] = useState({}); // Track which days are expanded in week view
  const [hoveredDay, setHoveredDay] = useState(null);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  useEffect(() => {
    fetchVisits();
    
    // Auto-expand today in week view
    if (view === "week") {
      const today = new Date();
      const weekStart = new Date(currentDate);
      const day = currentDate.getDay();
      weekStart.setDate(currentDate.getDate() - day);
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + i);
        if (date.toDateString() === today.toDateString()) {
          setExpandedDays({ [i]: true });
          break;
        }
      }
    }
  }, [currentDate, view]);

  const fetchVisits = async () => {
    setLoading(true);
    try {
      let startDate, endDate;

      if (view === "day") {
        startDate = new Date(currentDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(currentDate);
        endDate.setHours(23, 59, 59, 999);
      } else if (view === "week") {
        const day = currentDate.getDay();
        startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() - day);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
      } else {
        // Month view
        startDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        endDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
        endDate.setHours(23, 59, 59, 999);
      }

      const response = await api.get("/visits/by-date", {
        params: {
          // `aISO` lee los componentes locales: estas fechas se construyeron
          // en la zona del navegador y convertirlas correría el día.
          from: aISO(startDate),
          to: aISO(endDate),
        },
      });

      setVisits(response.data.visits);
    } catch (error) {
      console.error("Error fetching visits:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigate = (direction) => {
    const newDate = new Date(currentDate);

    if (view === "day") {
      newDate.setDate(newDate.getDate() + direction);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }

    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const toggleDayExpanded = (dayIndex) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayIndex]: !prev[dayIndex]
    }));
  };

  const formatDateHeader = () => {
    const options = { timeZone: "America/El_Salvador" };

    if (view === "day") {
      return currentDate.toLocaleDateString("es-SV", {
        ...options,
        weekday: isMobile ? "short" : "long",
        year: "numeric",
        month: isMobile ? "short" : "long",
        day: "numeric",
      });
    } else if (view === "week") {
      const weekStart = new Date(currentDate);
      const day = currentDate.getDay();
      weekStart.setDate(currentDate.getDate() - day);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      return `${weekStart.toLocaleDateString("es-SV", {
        ...options,
        day: "numeric",
        month: "short",
      })} - ${weekEnd.toLocaleDateString("es-SV", {
        ...options,
        day: "numeric",
        month: "short",
        year: isMobile ? "2-digit" : "numeric",
      })}`;
    } else {
      return currentDate.toLocaleDateString("es-SV", {
        ...options,
        year: "numeric",
        month: "long",
      });
    }
  };

  const getVisitsForDate = (date) => {
    /* Se comparan los dos días como texto `YYYY-MM-DD`, cada uno leído en la
       zona que le corresponde, y no con `toDateString()`.
       `visit.date` llega del API como medianoche UTC —se escogió en un
       `<input type="date">`—, así que en un navegador en UTC-6
       `toDateString()` daba el día anterior y la visita caía en la casilla
       equivocada del calendario. `date`, en cambio, lo construye este
       componente con componentes locales (`new Date(año, mes, día)`), y ahí el
       día correcto es el local: de eso se encarga `aISO`. */
    const dia = aISO(date);
    return visits.filter((visit) => diaISO(visit.date) === dia);
  };

  const styles = {
    container: {
      width: "100%",
    },
    header: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "stretch" : "center",
      marginBottom: "var(--space-8)",
      gap: "var(--space-4)",
    },
    title: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      fontFamily: "var(--font-display)",
      fontSize: isMobile ? "var(--text-xl)" : "var(--text-2xl)",
      fontWeight: "700",
      color: "var(--text)",
      marginBottom: isMobile ? "var(--space-2)" : "0",
    },
    controls: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "stretch" : "center",
      gap: isMobile ? "var(--space-3)" : "var(--space-4)",
    },
    viewSelector: {
      display: "flex",
      gap: "0.25rem",
      backgroundColor: "var(--bg-elevated)",
      padding: "0.25rem",
      borderRadius: "var(--radius-lg)",
      border: "1px solid var(--border)",
      width: isMobile ? "100%" : "auto",
    },
    viewButton: {
      flex: isMobile ? 1 : "none",
      padding: isMobile ? "0.5rem 0.75rem" : "0.5rem 1rem",
      fontSize: "var(--text-sm)",
      fontWeight: "500",
      backgroundColor: "transparent",
      border: "none",
      borderRadius: "var(--radius-md)",
      color: "var(--text-secondary)",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    viewButtonActive: {
      backgroundColor: "var(--gold)",
      color: "white",
    },
    navigationWrapper: {
      display: "flex",
      gap: "var(--space-3)",
      width: isMobile ? "100%" : "auto",
      alignItems: "center",
    },
    navigation: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "var(--space-2)" : "var(--space-4)",
      flex: 1,
    },
    navButton: {
      padding: isMobile ? "0.375rem" : "0.5rem",
      borderRadius: "var(--radius-md)",
      border: "1px solid var(--border)",
      backgroundColor: "var(--bg-elevated)",
      color: "var(--text)",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    currentDate: {
      fontFamily: "var(--font-body)",
      fontSize: isMobile ? "var(--text-sm)" : "var(--text-lg)",
      fontWeight: "600",
      color: "var(--text)",
      flex: 1,
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    todayButton: {
      padding: "0.5rem 1rem",
      fontSize: "var(--text-sm)",
      fontWeight: "500",
      backgroundColor: "var(--gold)",
      color: "white",
      border: "none",
      borderRadius: "var(--radius-md)",
      cursor: "pointer",
      transition: "all 0.2s ease",
      whiteSpace: "nowrap",
    },
    calendarContainer: {
      overflow: "auto",
      WebkitOverflowScrolling: "touch",
    },
    weekHeader: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: isMobile ? "2px" : "0.5rem",
      marginBottom: isMobile ? "0.25rem" : "0.5rem",
    },
    weekDay: {
      padding: isMobile ? "0.25rem" : "0.75rem",
      textAlign: "center",
      fontWeight: "600",
      fontSize: isMobile ? "var(--text-xs)" : "var(--text-sm)",
      color: "var(--text-secondary)",
      backgroundColor: "var(--bg-secondary)",
      borderRadius: "var(--radius-md)",
    },
    monthGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: isMobile ? "2px" : "0.5rem",
    },
    dayCell: {
      minHeight: isMobile ? "60px" : "100px",
      padding: isMobile ? "0.25rem" : "0.75rem",
      backgroundColor: "var(--bg-elevated)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)",
      cursor: "pointer",
      transition: "all 0.2s ease",
      position: "relative",
      overflow: "hidden",
    },
    dayCellToday: {
      backgroundColor: "var(--gold-subtle)",
      borderColor: "var(--gold)",
    },
    dayCellOtherMonth: {
      opacity: 0.5,
    },
    dayNumber: {
      fontWeight: "600",
      fontSize: isMobile ? "var(--text-xs)" : "var(--text-sm)",
      color: "var(--text)",
      marginBottom: isMobile ? "0.25rem" : "0.5rem",
    },
    dayVisits: {
      fontSize: "var(--text-xs)",
      color: "var(--text-secondary)",
    },
    visitCount: {
      display: "flex",
      alignItems: "center",
      gap: "0.25rem",
      marginTop: "0.25rem",
    },
    visitCountText: {
      fontSize: isMobile ? "10px" : "var(--text-xs)",
    },
    weekView: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(7, 1fr)",
      gap: isMobile ? "var(--space-3)" : "var(--space-4)",
    },
    dayColumn: {
      backgroundColor: "var(--bg-elevated)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)",
      padding: isMobile ? "var(--space-3)" : "var(--space-4)",
      minHeight: "auto",
      marginBottom: isMobile ? "var(--space-2)" : "0",
    },
    dayHeader: {
      fontWeight: "600",
      fontSize: "var(--text-sm)",
      color: "var(--text)",
      marginBottom: "0.75rem",
      paddingBottom: "0.75rem",
      borderBottom: "1px solid var(--border)",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      transition: "all 0.2s ease",
      userSelect: "none",
    },
    dayHeaderHover: {
      color: "var(--gold)",
    },
    accordionIcon: {
      transition: "transform 0.2s ease",
    },
    accordionIconRotated: {
      transform: "rotate(180deg)",
    },
    dayContent: {
      maxHeight: "0",
      overflow: "hidden",
      transition: "max-height 0.3s ease-out",
    },
    dayContentExpanded: {
      maxHeight: "1000px",
    },
    visitCount: {
      fontSize: "var(--text-xs)",
      color: "var(--text-secondary)",
      marginLeft: "auto",
      marginRight: "0.5rem",
      fontWeight: "400",
    },
    dayViewContainer: {
      backgroundColor: "var(--bg-elevated)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)",
      padding: isMobile ? "var(--space-4)" : "var(--space-6)",
    },
    visitsList: {
      display: "flex",
      flexDirection: "column",
      gap: "0.75rem",
    },
    visitItem: {
      padding: isMobile ? "var(--space-3)" : "var(--space-4)",
      backgroundColor: "var(--bg-secondary)",
      borderRadius: "var(--radius-md)",
      border: "1px solid var(--border-light)",
      transition: "all 0.2s ease",
    },
    visitItemHover: {
      transform: "translateY(-2px)",
      boxShadow: "var(--shadow-md)",
      borderColor: "var(--gold)",
    },
    visitName: {
      fontWeight: "600",
      fontSize: isMobile ? "var(--text-sm)" : "var(--text-base)",
      color: "var(--text)",
      marginBottom: "0.25rem",
    },
    visitHouse: {
      fontSize: "var(--text-xs)",
      color: "var(--text-secondary)",
      marginBottom: "0.5rem",
    },
    emptyState: {
      textAlign: "center",
      padding: "3rem",
      color: "var(--text-tertiary)",
    },
    mobileVisitPreview: {
      fontSize: "10px",
      marginTop: "0.125rem",
      color: "var(--text-muted)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      if (date.getMonth() !== month && i > 35) break;

      const dayVisits = getVisitsForDate(date);
      const isToday = date.toDateString() === today.toDateString();
      const isOtherMonth = date.getMonth() !== month;

      days.push(
        <div
          key={i}
          style={{
            ...styles.dayCell,
            ...(isToday ? styles.dayCellToday : {}),
            ...(isOtherMonth ? styles.dayCellOtherMonth : {}),
          }}
          onClick={() => {
            setCurrentDate(date);
            setView("day");
          }}
        >
          <div style={styles.dayNumber}>{date.getDate()}</div>
          {dayVisits.length > 0 && (
            <div style={styles.dayVisits}>
              <div style={styles.visitCount}>
                <Users size={isMobile ? 12 : 14} />
                <span style={styles.visitCountText}>
                  {dayVisits.length}
                </span>
              </div>
              {!isMobile && (
                <>
                  {dayVisits.slice(0, 2).map((visit) => (
                    <div
                      key={visit._id}
                      style={styles.mobileVisitPreview}
                    >
                      • {visit.visitorName}
                    </div>
                  ))}
                  {dayVisits.length > 2 && (
                    <div
                      style={{
                        ...styles.mobileVisitPreview,
                        color: "var(--gold)",
                      }}
                    >
                      +{dayVisits.length - 2} más
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={styles.calendarContainer}>
        <div style={styles.weekHeader}>
          {(isMobile ? ["D", "L", "M", "M", "J", "V", "S"] : ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]).map((day) => (
            <div key={day} style={styles.weekDay}>
              {day}
            </div>
          ))}
        </div>
        <div style={styles.monthGrid}>{days}</div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = new Date(currentDate);
    const day = currentDate.getDay();
    weekStart.setDate(currentDate.getDate() - day);

    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      const dayVisits = getVisitsForDate(date);
      const isExpanded = expandedDays[i] || false;
      const isToday = date.toDateString() === new Date().toDateString();

      days.push(
        <div key={i} style={{
          ...styles.dayColumn,
          ...(isToday ? { backgroundColor: "var(--gold-subtle)" } : {}),
        }}>
          <div 
            style={{
              ...styles.dayHeader,
              ...(hoveredDay === i ? styles.dayHeaderHover : {}),
            }}
            onClick={() => toggleDayExpanded(i)}
            onMouseEnter={() => setHoveredDay(i)}
            onMouseLeave={() => setHoveredDay(null)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {date.toLocaleDateString("es-SV", {
                weekday: isMobile ? "short" : "long",
                day: "numeric",
                month: isMobile ? "numeric" : "short",
                timeZone: "America/El_Salvador",
              })}
              {isToday && (
                <span style={{ 
                  fontSize: "var(--text-xs)", 
                  backgroundColor: "var(--gold)",
                  color: "white",
                  padding: "0.125rem 0.5rem",
                  borderRadius: "var(--radius-full)",
                  fontWeight: "600",
                }}>
                  HOY
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {dayVisits.length > 0 && (
                <span style={styles.visitCount}>
                  {dayVisits.length} visita{dayVisits.length !== 1 ? "s" : ""}
                </span>
              )}
              {dayVisits.length > 0 ? (
                isExpanded ? <ChevronUp size={18} style={styles.accordionIcon} /> : <ChevronDown size={18} style={styles.accordionIcon} />
              ) : null}
            </div>
          </div>
          <div style={{
            ...styles.dayContent,
            ...(isExpanded ? styles.dayContentExpanded : {}),
          }}>
            <div style={styles.visitsList}>
              {dayVisits.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "var(--text-tertiary)",
                    fontSize: "var(--text-xs)",
                    padding: "var(--space-2)",
                  }}
                >
                  Sin visitas programadas
                </div>
              ) : (
                dayVisits.map((visit) => (
                  <div
                    key={visit._id}
                    style={{
                      ...styles.visitItem,
                      ...(hoveredVisit === visit._id
                        ? styles.visitItemHover
                        : {}),
                    }}
                    onMouseEnter={() => setHoveredVisit(visit._id)}
                    onMouseLeave={() => setHoveredVisit(null)}
                  >
                    <div style={styles.visitName}>{visit.visitorName}</div>
                    <div style={styles.visitHouse}>
                      P{visit.polygon} - C{visit.houseNumber}
                    </div>
                    <StatusBadge status={visit.status} size="small" />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      );
    }

    return <div style={styles.weekView}>{days}</div>;
  };

  const renderDayView = () => {
    const dayVisits = getVisitsForDate(currentDate);

    return (
      <div style={styles.dayViewContainer}>
        {dayVisits.length === 0 ? (
          <div style={styles.emptyState}>
            <Calendar
              size={48}
              style={{ margin: "0 auto 1rem", opacity: 0.5 }}
            />
            <p>No hay visitas programadas para este día</p>
          </div>
        ) : (
          <div style={styles.visitsList}>
            {dayVisits.map((visit) => (
              <Card key={visit._id} hoverable>
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "stretch" : "flex-start",
                    gap: "var(--space-4)",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h4
                      style={{
                        fontSize: "var(--text-lg)",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {visit.visitorName}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.25rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          color: "var(--text-secondary)",
                          fontSize: "var(--text-sm)",
                        }}
                      >
                        <Users size={16} />
                        <span>
                          Polígono {visit.polygon} - Casa {visit.houseNumber}
                        </span>
                      </div>
                      {visit.note && (
                        <div
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--text-muted)",
                            marginTop: "0.5rem",
                          }}
                        >
                          Nota: {visit.note}
                        </div>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={visit.status} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          <Calendar size={isMobile ? 24 : 28} />
          Calendario de Visitas
        </h2>

        <div style={styles.controls}>
          <div style={styles.viewSelector}>
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  ...styles.viewButton,
                  ...(view === v ? styles.viewButtonActive : {}),
                }}
              >
                {v === "day" ? "Día" : v === "week" ? "Semana" : "Mes"}
              </button>
            ))}
          </div>

          <div style={styles.navigationWrapper}>
            <button onClick={goToToday} style={styles.todayButton}>
              Hoy
            </button>

            <div style={styles.navigation}>
              <button onClick={() => navigate(-1)} style={styles.navButton}>
                <ChevronLeft size={isMobile ? 18 : 20} />
              </button>
              <div style={styles.currentDate}>{formatDateHeader()}</div>
              <button onClick={() => navigate(1)} style={styles.navButton}>
                <ChevronRight size={isMobile ? 18 : 20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Card>
        {loading ? (
          <div style={styles.emptyState}>
            <div
              className="animate-spin"
              style={{
                width: "3rem",
                height: "3rem",
                border: "3px solid #e5e7eb",
                borderTopColor: "#3b82f6",
                borderRadius: "50%",
                margin: "0 auto",
              }}
            ></div>
          </div>
        ) : (
          <>
            {view === "month" && renderMonthView()}
            {view === "week" && (
              <>
                <div style={{ 
                  display: "flex", 
                  justifyContent: "flex-end", 
                  marginBottom: "var(--space-4)",
                  gap: "var(--space-2)",
                }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const allExpanded = {};
                      for (let i = 0; i < 7; i++) {
                        allExpanded[i] = true;
                      }
                      setExpandedDays(allExpanded);
                    }}
                  >
                    Expandir todos
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpandedDays({})}
                  >
                    Contraer todos
                  </Button>
                </div>
                {renderWeekView()}
              </>
            )}
            {view === "day" && renderDayView()}
          </>
        )}
      </Card>
    </div>
  );
}